<section class="flex flex-col" aria-label="P95 bandwidth utilization report">
  <div class="mx-auto w-full max-w-360 p-6 2xl:px-0">
    <div class="mb-2 flex flex-col gap-4">
      <div
        class="flex flex-col justify-between gap-2 md:flex-row md:items-start"
      >
        <div>
          <div>
            <a
              [routerLink]="['/services']"
              class="mb-1 inline-flex cursor-pointer items-center gap-1 rounded text-xs font-semibold tracking-widest text-blue-700 uppercase transition hover:text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1"
              aria-label="Back to Services"
            >
              <span
                class="material-icons-outlined text-base! leading-none"
                aria-hidden="true"
                >arrow_back</span
              >
              Services
            </a>
            <h1 id="page-title" class="m-0 text-3xl leading-tight font-bold">
              95th Percentile Bandwidth Utilization
            </h1>
            <p class="mt-1 text-sm text-gray-400">
              Monitor your peak bandwidth measured in 10-minute intervals,
              filtering out the top 5% spikes across all sites and connections
            </p>
          </div>
        </div>

        <div
          class="flex flex-wrap items-center gap-4 md:justify-end"
          role="toolbar"
          aria-label="actions"
        >
          @if (canShowSampleDataToggle()) {
            <div
              class="mt-2 flex h-8 items-center justify-center rounded-full bg-transparent px-1"
              aria-label="Data source toggle"
            >
              <app-toggle
                [ngModel]="showMockData()"
                (ngModelChange)="toggleMockData($event)"
                [label]="showMockData() ? 'Real Data' : 'Sample Data'"
                size="md"
                color="primary"
              >
              </app-toggle>
            </div>
          }

          <button
            type="button"
            [disabled]="!canExport() || isExporting()"
            class="btn-secondary h-9! px-4! text-xs!"
            [attr.aria-label]="
              isExporting() ? 'Exporting data' : 'Export P95 utilization'
            "
            (click)="exportToCsv()"
          >
            @if (isExporting()) {
              <svg
                class="mr-2 size-4 animate-spin text-black!"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Exporting...
            } @else {
              <mat-icon aria-hidden="true">download</mat-icon>
              <span>Export</span>
            }
          </button>
        </div>
      </div>

      <div class="flex flex-wrap items-end justify-between gap-3">
        <div
          class="relative w-full max-w-90 md:shrink-0"
          aria-label="Search and filter section"
        >
          <label for="p95-search" class="sr-only">Search sites</label>
          <mat-icon class="search-input-icon" aria-hidden="true"
            >search</mat-icon
          >
          <input
            id="p95-search"
            type="search"
            placeholder="Search"
            class="comcast-input mt-0! w-full pl-9 text-sm!"
            [value]="searchTerm$()"
            (input)="searchTerm$.set($any($event.target).value)"
            aria-label="Search sites"
          />
        </div>

        <div
          class="flex flex-wrap items-center gap-2 md:justify-end"
          role="group"
          aria-label="bandwidth filters"
        >
          <!-- Location Filter -->
          <div class="relative">
            <button
              type="button"
              id="p95-location-btn"
              class="filter-button"
              [ngClass]="{
                'border-blue-400 bg-white text-black': isLocationDropdownOpen(),
                'border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50':
                  !isLocationDropdownOpen(),
              }"
              [attr.aria-expanded]="isLocationDropdownOpen()"
              aria-haspopup="listbox"
              aria-controls="p95-location-listbox"
              (click)="toggleLocationDropdown($event)"
              (keydown.escape)="closeLocationDropdown()"
            >
              <mat-icon
                class="flex! h-4! w-4! items-center! justify-center! text-base!"
                aria-hidden="true"
              >
                location_on
              </mat-icon>
              <span class="min-w-0 truncate">
                Location:
                <strong>{{ selectedLocation$() || 'All' }}</strong>
              </span>
              <mat-icon
                class="flex! h-4! w-4! items-center! justify-center! text-base!"
                aria-hidden="true"
              >
                {{
                  isLocationDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
                }}
              </mat-icon>
            </button>

            @if (isLocationDropdownOpen()) {
              <div
                id="p95-location-listbox"
                class="comcast-select-panel min-w-48"
                [class.comcast-select-panel--open]="isLocationDropdownOpen()"
                role="listbox"
                aria-label="Filter by location"
                (click)="$event.stopPropagation()"
                (keydown.escape)="closeLocationDropdown()"
              >
                <div class="comcast-select-options">
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      !selectedLocation$()
                    "
                    role="option"
                    [attr.aria-selected]="!selectedLocation$()"
                    (click)="clearLocation()"
                  >
                    All Locations
                  </button>
                  @for (location of uniqueLocations$(); track location) {
                    <button
                      type="button"
                      class="comcast-select-option w-full text-left"
                      [class.comcast-select-option--selected]="
                        selectedLocation$() === location
                      "
                      role="option"
                      [attr.aria-selected]="selectedLocation$() === location"
                      (click)="setLocation(location)"
                    >
                      {{ location }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Duration Dropdown -->
          <div class="relative">
            <button
              type="button"
              id="p95-duration-btn"
              class="filter-button"
              [ngClass]="{
                'border-blue-400 bg-white text-black': isDurationDropdownOpen(),
                'border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50':
                  !isDurationDropdownOpen(),
              }"
              [attr.aria-expanded]="isDurationDropdownOpen()"
              aria-haspopup="listbox"
              aria-controls="p95-duration-listbox"
              aria-describedby="p95-duration-hint"
              (click)="toggleDurationDropdown($event)"
              (keydown.escape)="closeDurationDropdown()"
            >
              <span class="min-w-0 truncate">
                Duration:
                <strong>{{
                  selectedDuration$() === '1w'
                    ? '1 Week'
                    : selectedDuration$() === '1m'
                      ? '1 Month'
                      : '1 Quarter'
                }}</strong>
              </span>
              <mat-icon
                class="flex! h-4! w-4! items-center! justify-center! text-base!"
                aria-hidden="true"
              >
                {{
                  isDurationDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
                }}
              </mat-icon>
            </button>

            @if (isDurationDropdownOpen()) {
              <div
                id="p95-duration-listbox"
                class="comcast-select-panel min-w-48"
                [class.comcast-select-panel--open]="isDurationDropdownOpen()"
                role="listbox"
                aria-label="Select report duration"
                (click)="$event.stopPropagation()"
                (keydown.escape)="closeDurationDropdown()"
              >
                <div class="comcast-select-options">
                  @for (option of durationOptions; track option.value) {
                    <button
                      type="button"
                      class="comcast-select-option w-full text-left"
                      [class.comcast-select-option--selected]="
                        selectedDuration$() === option.value
                      "
                      role="option"
                      [attr.aria-selected]="
                        selectedDuration$() === option.value
                      "
                      (click)="selectDuration(option.value)"
                    >
                      {{ option.label }}
                    </button>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Per Page Filter -->
          <div class="relative">
            <button
              type="button"
              id="p95-page-size-btn"
              class="filter-button"
              [ngClass]="{
                'border-blue-400 bg-white text-black': isPageSizeDropdownOpen(),
                'border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50':
                  !isPageSizeDropdownOpen(),
              }"
              [attr.aria-expanded]="isPageSizeDropdownOpen()"
              aria-haspopup="listbox"
              aria-controls="p95-page-size-listbox"
              (click)="togglePageSizeDropdown($event)"
              (keydown.escape)="closePageSizeDropdown()"
            >
              <mat-icon
                class="flex! h-4! w-4! items-center! justify-center! text-base!"
                aria-hidden="true"
              >
                view_agenda
              </mat-icon>
              <span
                >Per page: <strong>{{ pageSize$() }}</strong></span
              >
              <mat-icon
                class="flex! h-4! w-4! items-center! justify-center! text-base!"
                aria-hidden="true"
              >
                {{
                  isPageSizeDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
                }}
              </mat-icon>
            </button>

            @if (isPageSizeDropdownOpen()) {
              <div
                id="p95-page-size-listbox"
                class="comcast-select-panel min-w-48"
                [class.comcast-select-panel--open]="isPageSizeDropdownOpen()"
                role="listbox"
                aria-label="Items per page options"
                (click)="$event.stopPropagation()"
                (keydown.escape)="closePageSizeDropdown()"
              >
                <div class="comcast-select-options">
                  @for (option of pageSizeOptions; track option) {
                    <button
                      type="button"
                      class="comcast-select-option w-full text-left"
                      [class.comcast-select-option--selected]="
                        pageSize$() === option
                      "
                      role="option"
                      [attr.aria-selected]="pageSize$() === option"
                      (click)="onPageSizeChange(option)"
                    >
                      {{ option }} items per page
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>

      <p id="p95-duration-hint" class="sr-only">
        Changing the duration reloads the report.
      </p>

      <div
        class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
      >
        <aside
          class="w-full text-xs md:w-auto md:flex-none"
          aria-labelledby="heatmap-legend-heading"
        >
          <h2 id="heatmap-legend-heading" class="sr-only">Color Legend</h2>
          <div class="space-y-1.5">
            <div class="flex items-center gap-2">
              <span class="text-sm font-bold">Low</span>
              <div
                class="from-heat-blue-2 via-heat-mid-1 h-3 w-46 cursor-help rounded-lg bg-linear-to-r to-orange-600 shadow-inner"
                aria-hidden="true"
                matTooltip="Blue = low, purple = medium, red = high. Shows combined incoming and outgoing bandwidth usage."
                matTooltipPosition="above"
              ></div>
              <span class="text-sm font-bold">High</span>
            </div>
            <p class="sr-only">
              Blue indicates low utilization, purple indicates medium
              utilization, and red indicates high utilization. Shows combined
              incoming and outgoing bandwidth usage.
            </p>
          </div>
        </aside>

        <app-filter-summary
          [itemCount]="filteredItemsCount$()"
          itemLabel="records"
          [parts]="filterParts$()"
          [hasFilters]="
            !!selectedLocation$() ||
            !!searchTerm$() ||
            selectedDuration$() !== '1m'
          "
          (clearAll)="clearAllFilters()"
        ></app-filter-summary>
      </div>
    </div>
    <div
      class="flex flex-col gap-3"
      aria-label="Utilization data display section"
    >
      <div
        class="flex w-full flex-col items-stretch overflow-hidden pb-4 md:bg-white md:shadow-sm"
        aria-label="Data table and pagination"
      >
        <app-p95-utilization-list
          [observations$]="observations$"
          [searchTerm]="searchTerm$()"
          [selectedLocation]="selectedLocation$()"
          [pageIndex]="pageIndex$()"
          [pageSize]="pageSize$()"
          class="mb-6 block w-full max-w-none self-stretch"
        />

        @if (!observations$.loading$()) {
          <app-pagination
            [currentPage]="pageIndex$() + 1"
            [totalPages]="totalPages$()"
            [itemsPerPage]="pageSize$()"
            (pageChange)="onPageChange($event - 1)"
          ></app-pagination>
        }
      </div>
    </div>
  </div>
</section>
